// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { IS_NATIVE, isAndroid } from './platform.js';

// DECISION: All native-shell chrome setup happens once, imperatively, at app
// mount. Keeping it out of React render avoids re-applying status-bar/keyboard
// config on every re-render, and every call is guarded so the web build (where
// these plugins are unimplemented) is a clean no-op.

let initialized = false;

export async function initNativeShell(): Promise<void> {
  if (!IS_NATIVE || initialized) return;
  initialized = true;

  try {
    // Light glyphs on the dark #0F1117 background.
    await StatusBar.setStyle({ style: Style.Dark });
    if (isAndroid()) {
      // Android lets us paint the status-bar background to match the app bg so
      // the notch area is seamless. iOS derives it from the WebView content.
      await StatusBar.setBackgroundColor({ color: '#0F1117' });
      // DECISION: overlay=false so content lays out below the status bar; we
      // still add safe-area padding in CSS for the notch/cutout cases.
      await StatusBar.setOverlaysWebView({ overlay: false });
    }
  } catch {
    /* status bar plugin unavailable */
  }

  try {
    // Toggle a body class while the keyboard is visible so layouts can lift
    // sticky footers (e.g. the quick-entry Save bar) above it. resize:'native'
    // (capacitor.config.ts) already shrinks the WebView; this is for fine-tuning.
    void Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-open');
    });
    void Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-open');
    });
  } catch {
    /* keyboard plugin unavailable */
  }

  try {
    // Splash is configured to auto-hide (capacitor.config.ts); hide explicitly
    // once the web layer is up in case auto-hide is disabled on a platform.
    await SplashScreen.hide();
  } catch {
    /* splash plugin unavailable */
  }
}
